/**
 * 爆款模板 — 接口调用
 */
import type { TplTemplateVO } from '@/3.0/ai-viral-template/_components/template-ui/types';
import type {
  ViralTemplateFormApiResponse,
  ViralTemplateTaskSubmitBody,
} from '@/3.0/ai-viral-template/utils/templateForm';
import request from '@/utils/request';

import type { ApiResponse } from '.';
import {
  fetchViralTemplateFormMock,
  fetchViralTemplatePageMock,
  submitViralTemplateTaskMock,
  VIRAL_TEMPLATE_USE_MOCK,
} from './viralTemplate.mock';

// re-export mock 开关（DrawerTaskPanel mock 和 my-creations 页面依赖）
export { VIRAL_TEMPLATE_USE_MOCK } from './viralTemplate.mock';

type ViralTemplateApiItem = {
  id: number;
  name: string;
  description?: string;
  category: string;
  preview: string;
  cover: string;
  createTime: number;
  updateTime: number;
  modelFee?: {
    amount?: number;
    basePrice?: number;
    discount?: number;
    modelCode?: string;
    modelId?: number;
    ruleId?: number;
    ruleName?: string;
  };
};

function normalizeTmplPageResponse(
  res: Record<string, unknown> | null | undefined,
  args: { pageNum: number; pageSize: number },
): ApiResponse<TplTemplateVO> {
  const raw = res as { items?: unknown; data?: { items?: unknown } } | null | undefined;
  const rawItems = (raw?.items || raw?.data?.items || []) as ViralTemplateApiItem[];
  const mappedItems: TplTemplateVO[] = rawItems.map((item) => ({
    id: item.id,
    name: item.name,
    description: item.description ?? '',
    category: item.category,
    preview: item.preview,
    cover: item.cover,
    createTime: item.createTime,
    updateTime: item.updateTime,
    modelFee: item.modelFee
      ? {
          amount: Number(item.modelFee.amount ?? 0),
          basePrice: item.modelFee.basePrice,
          discount: item.modelFee.discount,
          modelCode: item.modelFee.modelCode,
          modelId: item.modelFee.modelId,
          ruleId: item.modelFee.ruleId,
          ruleName: item.modelFee.ruleName,
        }
      : undefined,
  }));

  return {
    ...(res ?? {}),
    code: (res?.code as number) ?? 0,
    success: (res?.success as boolean) ?? false,
    items: mappedItems,
    totalCount: (res?.totalCount as number) ?? mappedItems.length,
    hasMore: (res?.hasMore as boolean) ?? false,
    page: (res?.page as number) ?? args.pageNum,
    pageSize: (res?.pageSize as number) ?? args.pageSize,
    message: res?.message as string | undefined,
  };
}

/** 分页查询模版 */
export const fetchViralTemplatePage = async (args: {
  category?: string;
  pageNum: number;
  pageSize: number;
}): Promise<ApiResponse<TplTemplateVO>> => {
  if (VIRAL_TEMPLATE_USE_MOCK) {
    return fetchViralTemplatePageMock(args);
  }

  const data: Record<string, unknown> = {
    pageNum: args.pageNum,
    pageSize: args.pageSize,
  };
  if (args.category) data.category = args.category;

  const res = await request('/api/rest/dlt/f/cloud/tmpl/page', {
    method: 'POST',
    data,
  });

  return normalizeTmplPageResponse(res as Record<string, unknown>, args);
};

/**
 * 首页模板条：请求同源 `/api/landing/viral-templates`（服务端转发 tmpl/page，无登录态、不经 axios）。
 */
export const fetchViralTemplatePageForLanding = async (args: {
  pageNum: number;
  pageSize: number;
  signal?: AbortSignal;
}): Promise<ApiResponse<TplTemplateVO>> => {
  if (VIRAL_TEMPLATE_USE_MOCK) {
    return fetchViralTemplatePageMock(args);
  }

  const qs = new URLSearchParams({
    pageNum: String(args.pageNum),
    pageSize: String(args.pageSize),
  });

  const response = await fetch(`/api/landing/viral-templates?${qs}`, {
    signal: args.signal,
  });
  let json: Record<string, unknown> = {};
  try {
    json = (await response.json()) as Record<string, unknown>;
  } catch {
    json = {};
  }

  return normalizeTmplPageResponse(json, args);
};

/** 模板详情表单 */
export const fetchViralTemplateForm = async (args: {
  id: number;
}): Promise<ViralTemplateFormApiResponse> => {
  if (VIRAL_TEMPLATE_USE_MOCK) {
    return fetchViralTemplateFormMock(args);
  }
  return request('/api/rest/dlt/f/cloud/tmpl/form', {
    method: 'POST',
    data: args,
  }) as Promise<ViralTemplateFormApiResponse>;
};

/** 提交模板任务 */
export const submitViralTemplateTask = async (
  body: ViralTemplateTaskSubmitBody,
): Promise<ViralTemplateFormApiResponse> => {
  if (VIRAL_TEMPLATE_USE_MOCK) {
    return submitViralTemplateTaskMock();
  }
  return request('api/rest/dlt/f/cloud/tmpl/task', {
    method: 'POST',
    data: body,
  }) as Promise<ViralTemplateFormApiResponse>;
};
